import { PERMISSIONS } from "@/lib/permissions";
import {
  ALLURA_CASH_REGISTER_TENANT_SUBDOMAINS,
  RENU_SALES_TAX_DETAILS_TENANT_SUBDOMAINS,
} from "@/lib/tenant-flags";

import { Module } from "@/types/tenant";

/**
 * Metadata for every built-in analytics dashboard, with no component imports so
 * the sitemap and navigation can use it without pulling the dashboards into
 * every bundle. `registry.ts` joins these entries with their components.
 */
export interface AnalyticsDashboardMeta {
  id: string;
  name: string;
  description: string;
  keywords: string[];
  group: AnalyticsGroupName;
  permissions: string[];
  /** The feature permission the dashboard's data is also gated on, server-side. */
  allPermissions?: string[];
  /** Show when the tenant has any one of these modules enabled. */
  requiredModules?: Module[];
  /** Only show for tenants whose subDomain includes one of these strings */
  tenantSubDomains?: string[];
  /** Legacy paths that still resolve here, so the redirect stubs stay gated. */
  routeAliases?: string[];
  /**
   * Sub-views the dashboard exposes through the `section` query param. When a
   * dashboard has these, the nav lists each one in its own right rather than
   * hiding them behind tabs inside the page.
   */
  sections?: AnalyticsDashboardSection[];
}

export interface AnalyticsDashboardSection {
  id: string;
  name: string;
  description: string;
  keywords: string[];
  /** Also required for this section, on top of the dashboard's modules. */
  requiredModule?: Module;
}

export const ANALYTICS_GROUPS = [
  "Revenue",
  "Patients",
  "Team",
  "Operations",
  "Growth",
] as const;

export type AnalyticsGroupName = (typeof ANALYTICS_GROUPS)[number];

export const ANALYTICS_ROOT = "/analytics";

export function analyticsDashboardRoute(id: string): string {
  return `${ANALYTICS_ROOT}/${id}`;
}

export const analyticsDashboardMeta: AnalyticsDashboardMeta[] = [
  {
    id: "daily",
    name: "Daily Clinic Dashboard",
    description:
      "Today's collections, the month so far, staff contribution, no-shows and new memberships",
    keywords: ["daily", "today", "clinic", "kpi", "collections", "home"],
    group: "Revenue",
    permissions: [PERMISSIONS.BILLING_REPORTS_READ],
    requiredModules: ["DAILY_CLINIC_DASHBOARD"],
  },
  {
    id: "practice-lead",
    name: "Practice Lead Dashboard",
    description:
      "One location's revenue against goal, patients, memberships, reviews and today's action lists",
    keywords: [
      "practice",
      "lead",
      "recap",
      "goal",
      "pace",
      "rebook",
      "front desk",
      "pec",
    ],
    group: "Revenue",
    permissions: [PERMISSIONS.ANALYTICS_DASHBOARD_PRACTICE_LEAD_READ],
    requiredModules: ["LOCATION_DASHBOARDS"],
  },
  {
    id: "location-scorecard",
    name: "Location Performance Scorecard",
    description:
      "Every location against goal and bonus tier, five KPIs, and the reasons behind them",
    keywords: [
      "scorecard",
      "location",
      "goal",
      "tier",
      "bonus",
      "kpi",
      "ceo",
      "total",
    ],
    group: "Revenue",
    permissions: [PERMISSIONS.ANALYTICS_DASHBOARD_LOCATION_SCORECARD_READ],
    requiredModules: ["LOCATION_DASHBOARDS"],
  },
  {
    id: "billing",
    name: "Billing Dashboard",
    description:
      "Revenue, collections, and performance across locations, providers, and payment methods",
    keywords: [
      "billing",
      "revenue",
      "collections",
      "dashboard",
      "deferred",
      "performance",
    ],
    group: "Revenue",
    permissions: [PERMISSIONS.BILLING_REPORTS_READ],
    requiredModules: ["PAYMENTS"],
    routeAliases: ["/billing/dashboard"],
  },
  {
    id: "product-sales-breakdown",
    name: "Product Sales Breakdown",
    description:
      "Detailed breakdown of revenue by products, services, and transactions",
    keywords: ["product", "sales", "breakdown", "analytics", "dashboard"],
    group: "Revenue",
    permissions: [PERMISSIONS.ANALYTICS_DASHBOARD_PRODUCT_SALES_BREAKDOWN_READ],
    requiredModules: ["PAYMENTS"],
  },
  {
    id: "payment-breakdown",
    name: "Payment Breakdown",
    description: "Breakdown of payments by payment method",
    keywords: ["payment", "breakdown", "analytics", "dashboard", "methods"],
    group: "Revenue",
    permissions: [PERMISSIONS.ANALYTICS_DASHBOARD_PAYMENT_BREAKDOWN_READ],
    requiredModules: ["PAYMENTS"],
  },
  {
    id: "accounting-overview",
    name: "Accounting Overview",
    description:
      "Deferred revenue, liability roll-forward, cross-location redemption flows, and recognized revenue breakdown",
    keywords: [
      "accounting",
      "deferred",
      "revenue",
      "liability",
      "recognition",
      "analytics",
    ],
    group: "Revenue",
    permissions: [PERMISSIONS.ANALYTICS_DASHBOARD_ACCOUNTING_OVERVIEW_READ],
    requiredModules: ["PAYMENTS"],
  },
  {
    id: "cash-register-report",
    name: "Daily Sales & Collections",
    description:
      "One-day view of sales, collections, liability, redemptions, and discounts — grouped by category and payment method with a per-location breakdown.",
    keywords: [
      "cash register",
      "daily",
      "sales",
      "collections",
      "redemptions",
      "discounts",
    ],
    group: "Revenue",
    permissions: [PERMISSIONS.ANALYTICS_DASHBOARD_CASH_REGISTER_REPORT_READ],
    requiredModules: ["PAYMENTS"],
    tenantSubDomains: [...ALLURA_CASH_REGISTER_TENANT_SUBDOMAINS],
  },
  {
    id: "cash-register-drill-down",
    name: "Sales Line Items",
    description:
      "Every line item that rolls into Daily Sales & Collections — invoice, patient, item, quantity, and net — with clickable filters for reconciliation.",
    keywords: [
      "sales",
      "line items",
      "invoice",
      "reconciliation",
      "drill down",
    ],
    group: "Revenue",
    permissions: [
      PERMISSIONS.ANALYTICS_DASHBOARD_CASH_REGISTER_DRILL_DOWN_READ,
    ],
    requiredModules: ["PAYMENTS"],
    tenantSubDomains: [...ALLURA_CASH_REGISTER_TENANT_SUBDOMAINS],
  },
  {
    id: "payment-plan-analytics",
    name: "Payment Plan Analytics",
    description:
      "Track payment plan statuses, collection progress, and installment breakdowns",
    keywords: [
      "payment",
      "plan",
      "installments",
      "analytics",
      "dashboard",
      "collections",
    ],
    group: "Revenue",
    permissions: [PERMISSIONS.ANALYTICS_DASHBOARD_PAYMENT_PLAN_ANALYTICS_READ],
    requiredModules: ["PAYMENTS"],
  },
  {
    id: "credit-gift-card-activity",
    name: "Credits & Gift Cards Issued",
    description:
      "Every patient credit added and gift card created, with the staff member who did it",
    keywords: [
      "credit",
      "patient credit",
      "gift card",
      "issued",
      "audit",
      "staff",
      "adjustment",
    ],
    group: "Revenue",
    permissions: [PERMISSIONS.BILLING_REPORTS_READ],
    requiredModules: ["PAYMENTS"],
  },
  {
    id: "sales-tax-details",
    name: "Sales Tax Details",
    description: "Daily tax breakdowns by location with export to Excel",
    keywords: ["sales", "tax", "report", "tax details"],
    group: "Revenue",
    permissions: [PERMISSIONS.ANALYTICS_DASHBOARD_SALES_TAX_DETAILS_READ],
    allPermissions: [PERMISSIONS.BILLING_REPORTS_READ],
    requiredModules: ["PAYMENTS"],
    tenantSubDomains: [...RENU_SALES_TAX_DETAILS_TENANT_SUBDOMAINS],
  },
  {
    id: "check-in",
    name: "Check-In Dashboard",
    description:
      "Appointment volume, intake progress, and alerts for the clinic's day",
    keywords: [
      "check-in",
      "check in",
      "dashboard",
      "appointments",
      "intake",
      "front desk",
    ],
    group: "Patients",
    permissions: [PERMISSIONS.SCHEDULING_READ],
    requiredModules: ["SCHEDULING"],
    routeAliases: ["/check-in/dashboard"],
  },
  {
    id: "appointment-dashboard",
    name: "Appointment Dashboard",
    description:
      "Appointment statistics including volume, value, booking sources, cancellation rates, and detailed breakdown",
    keywords: [
      "appointment",
      "appointments",
      "analytics",
      "dashboard",
      "booking",
      "cancellation",
    ],
    group: "Patients",
    permissions: [PERMISSIONS.ANALYTICS_DASHBOARD_APPOINTMENT_DASHBOARD_READ],
    requiredModules: ["SCHEDULING"],
    sections: [
      {
        id: "appointments",
        name: "Appointment Volume",
        description:
          "Appointment counts and value, booking sources, and cancellation rates",
        keywords: ["appointments", "volume", "bookings", "cancellations"],
      },
      {
        id: "activity",
        name: "Appointment Activity",
        description:
          "Booking, rescheduling, and cancellation activity over time",
        keywords: ["activity", "rescheduled", "cancelled", "appointments"],
      },
      {
        id: "provider-appointment-hours",
        name: "Provider Hours",
        description: "Booked hours per provider against their available time",
        keywords: ["provider", "hours", "utilization", "capacity"],
      },
      {
        id: "rooms-devices",
        name: "Rooms & Devices",
        description: "Booked time per room and device",
        keywords: ["rooms", "devices", "equipment", "utilization", "capacity"],
      },
      {
        id: "patient-status-flow",
        name: "Patient Status Flow",
        requiredModule: "CALENDAR_PATIENT_STATUS",
        description:
          "How patients move through check-in, rooming, and checkout",
        keywords: ["patient status", "flow", "rooming", "wait time"],
      },
    ],
  },
  {
    id: "patient-breakdown",
    name: "Patient Dashboard",
    description:
      "Patient demographics, spending patterns, and inactive patient tracking",
    keywords: [
      "patient",
      "breakdown",
      "analytics",
      "dashboard",
      "demographics",
      "spending",
      "inactive",
      "patients",
      "age",
      "source",
    ],
    group: "Patients",
    permissions: [PERMISSIONS.ANALYTICS_DASHBOARD_PATIENT_BREAKDOWN_READ],
  },
  {
    id: "care-plan-analytics",
    name: "Care Plan Analytics",
    description:
      "Care plan adoption, completion funnels, retention impact, and revenue analytics",
    keywords: [
      "care",
      "plan",
      "treatment",
      "analytics",
      "dashboard",
      "completion",
      "retention",
    ],
    group: "Operations",
    permissions: [PERMISSIONS.ANALYTICS_DASHBOARD_CARE_PLAN_ANALYTICS_READ],
    requiredModules: ["CARE_PLANS"],
  },
  {
    id: "notes",
    name: "Note Analytics",
    description: "Analytics and reports for notes",
    keywords: ["notes", "analytics", "dashboard"],
    group: "Patients",
    permissions: [PERMISSIONS.ANALYTICS_DASHBOARD_NOTES_READ],
    requiredModules: ["SCRIBE"],
  },
  {
    id: "provider-earnings",
    name: "Provider Earnings",
    description:
      "Detailed breakdown of provider earnings including sales, discounts, tips, adjustments, and refunds",
    keywords: [
      "provider",
      "earnings",
      "report",
      "analytics",
      "dashboard",
      "commissions",
    ],
    group: "Team",
    permissions: [PERMISSIONS.ANALYTICS_DASHBOARD_PROVIDER_EARNINGS_READ],
    requiredModules: ["PAYMENTS"],
  },
  {
    id: "provider-productivity",
    name: "Provider Productivity",
    description:
      "Scheduled, blocked, available and booked hours per provider, with utilization, sales and revenue per hour, by location or day",
    keywords: [
      "provider",
      "productivity",
      "utilization",
      "hours",
      "revenue per hour",
      "booked",
      "scheduled",
      "blocked",
    ],
    group: "Team",
    permissions: [PERMISSIONS.ANALYTICS_DASHBOARD_PROVIDER_EARNINGS_READ],
    // Any listed module unlocks an entry, and the report is empty without shifts.
    requiredModules: ["SCHEDULING"],
  },
  {
    id: "provider-activity",
    name: "Provider Activity",
    description:
      "Comprehensive activity feed showing all provider-created items across the platform",
    keywords: [
      "provider",
      "activity",
      "analytics",
      "dashboard",
      "charges",
      "payments",
      "notes",
    ],
    group: "Team",
    permissions: [PERMISSIONS.ANALYTICS_DASHBOARD_PROVIDER_ACTIVITY_READ],
  },
  {
    id: "inventory",
    name: "Inventory Analytics",
    description:
      "On-hand stock and value, aging and expiry risk, product profitability by cost basis, and the full stock movement ledger",
    keywords: [
      "inventory",
      "stock",
      "current stock",
      "aging",
      "expiry",
      "ledger",
      "profitability",
      "cogs",
      "margin",
      "reorder",
      "vendor",
      "supplier",
      "analytics",
      "dashboard",
    ],
    group: "Operations",
    permissions: [PERMISSIONS.ANALYTICS_DASHBOARD_INVENTORY_READ],
    requiredModules: ["INVENTORY"],
    sections: [
      {
        id: "current-stock",
        name: "Current Stock",
        description: "On-hand quantity and stock value by product and location",
        keywords: ["current stock", "on hand", "stock value", "inventory"],
      },
      {
        id: "aging",
        name: "Inventory Aging",
        description: "Stock sitting too long and the value at expiry risk",
        keywords: ["aging", "expiry", "expiring", "shelf life", "inventory"],
      },
      {
        id: "profitability",
        name: "Inventory Profitability",
        description: "Product margin against actual lot cost or purchase price",
        keywords: ["profitability", "margin", "cogs", "cost", "inventory"],
      },
      {
        id: "ledger",
        name: "Stock Ledger",
        description:
          "Every stock movement — receipts, dispenses, and adjustments",
        keywords: ["ledger", "movement", "transactions", "stock", "inventory"],
      },
      {
        id: "lot-coverage",
        name: "Lot Coverage",
        description: "How consistently staff are recording lot numbers",
        keywords: ["lot", "coverage", "lot numbers", "compliance", "inventory"],
      },
    ],
  },
  {
    id: "memberships",
    name: "Memberships Analytics",
    description: "Analytics and reports for membership plans and members",
    keywords: [
      "memberships",
      "members",
      "plans",
      "analytics",
      "dashboard",
      "subscriptions",
    ],
    group: "Patients",
    permissions: [PERMISSIONS.ANALYTICS_DASHBOARD_MEMBERSHIPS_READ],
    allPermissions: [PERMISSIONS.MEMBERSHIPS_READ],
  },
  {
    id: "call-analytics",
    name: "Call Analytics",
    description:
      "Call volume, outcomes, staff activity, and AI performance metrics",
    keywords: ["call", "calling", "phone", "analytics", "dashboard"],
    group: "Growth",
    permissions: [PERMISSIONS.ANALYTICS_DASHBOARD_CALL_ANALYTICS_READ],
    allPermissions: [PERMISSIONS.MESSAGING_CALLS_READ],
    requiredModules: ["CALLING"],
  },
  {
    id: "blast-analytics",
    name: "Blast Analytics",
    description:
      "SMS and email blast volume, spend, delivery, and engagement metrics",
    keywords: ["blast", "sms", "email", "marketing", "analytics", "dashboard"],
    group: "Growth",
    permissions: [PERMISSIONS.ANALYTICS_DASHBOARD_BLAST_ANALYTICS_READ],
    requiredModules: ["TEXTING", "EMAILS"],
  },
];

/**
 * Any one of these is enough to see the Analytics section at all — the union of
 * what its dashboards require, so a user gated to a single dashboard (Check-In,
 * say) still reaches the section that holds it.
 */
export const ANALYTICS_SECTION_PERMISSIONS: string[] = Array.from(
  new Set([
    PERMISSIONS.ANALYTICS_READ,
    ...analyticsDashboardMeta.flatMap((dashboard) => dashboard.permissions),
  ]),
);

/**
 * Custom analytics or any built-in dashboard reaches Scheduled Reports; the
 * server then lists only the schedules for reports the user can open.
 */
export const SCHEDULED_REPORTS_PERMISSIONS: string[] = [
  PERMISSIONS.ANALYTICS_READ,
  ...Object.values(PERMISSIONS).filter((permission) =>
    permission.startsWith("analytics:dashboards:"),
  ),
];

/**
 * Params that describe the report you were just looking at rather than the
 * question you were asking, so they do not follow you to the next one. A sort
 * column belongs to one table's columns; everything else — dates, locations,
 * grouping — is the comparison the user is in the middle of making.
 */
const VIEW_SCOPED_PARAMS = new Set([
  "section",
  "sortBy",
  "sortDir",
  "sortDirection",
  "page",
  "perPage",
  "from",
]);

/**
 * Build the href for an analytics destination, carrying the filters already in
 * force. Comparing last week's product sales against last week's payments means
 * moving between two dashboards without re-picking the date range each time.
 */
export function carryAnalyticsParams(
  route: string,
  current: URLSearchParams | null | undefined,
): string {
  if (!current) return route;

  const [path, ownSearch] = route.split("?");
  const next = new URLSearchParams(ownSearch);

  current.forEach((value, key) => {
    if (VIEW_SCOPED_PARAMS.has(key)) return;
    if (next.has(key)) return;
    next.append(key, value);
  });

  const search = next.toString();
  return search ? `${path}?${search}` : path;
}

/** One nav destination: a whole dashboard, or one of its sections. */
export interface AnalyticsNavEntry {
  id: string;
  name: string;
  description: string;
  keywords: string[];
  group: AnalyticsGroupName;
  permissions: string[];
  allPermissions?: string[];
  requiredModules?: Module[];
  sectionModule?: Module;
  tenantSubDomains?: string[];
  route: string;
  routeAliases?: string[];
}

/**
 * Flatten a dashboard into the rows the nav shows for it: one per section when
 * it has them, otherwise the dashboard itself. The first section also answers
 * to the bare dashboard route, which is where that section already loads.
 */
export function analyticsNavEntries(
  dashboard: AnalyticsDashboardMeta,
): AnalyticsNavEntry[] {
  const base = {
    group: dashboard.group,
    permissions: dashboard.permissions,
    allPermissions: dashboard.allPermissions,
    requiredModules: dashboard.requiredModules,
    tenantSubDomains: dashboard.tenantSubDomains,
  };

  if (!dashboard.sections?.length) {
    return [
      {
        ...base,
        id: dashboard.id,
        name: dashboard.name,
        description: dashboard.description,
        keywords: dashboard.keywords,
        route: analyticsDashboardRoute(dashboard.id),
        routeAliases: dashboard.routeAliases,
      },
    ];
  }

  return dashboard.sections.map((section, index) => ({
    ...base,
    id: `${dashboard.id}:${section.id}`,
    name: section.name,
    description: section.description,
    keywords: [...section.keywords, ...dashboard.keywords],
    sectionModule: section.requiredModule,
    route: `${analyticsDashboardRoute(dashboard.id)}?section=${section.id}`,
    routeAliases:
      index === 0
        ? [
            analyticsDashboardRoute(dashboard.id),
            ...(dashboard.routeAliases ?? []),
          ]
        : dashboard.routeAliases,
  }));
}

export function getAnalyticsDashboardMeta(
  id: string,
): AnalyticsDashboardMeta | undefined {
  return analyticsDashboardMeta.find((dashboard) => dashboard.id === id);
}

/** Nav destinations in sidebar order, grouped, with empty groups dropped. */
export function groupAnalyticsDashboards(
  dashboards: AnalyticsDashboardMeta[],
): { name: AnalyticsGroupName; entries: AnalyticsNavEntry[] }[] {
  const entries = dashboards.flatMap(analyticsNavEntries);
  return ANALYTICS_GROUPS.map((name) => ({
    name,
    entries: entries.filter((entry) => entry.group === name),
  })).filter((group) => group.entries.length > 0);
}
